import { runtimeMonitor, type RuntimeMonitor, type RuntimeStorage } from './runtime'
import { runtimeUrl } from './protocol'
import { isVisible, onVisibilityChange } from './visibility'

export type BrowserRuntimeOptions = {
  app: string
  version?: string
  /** Router location pathname at start; the composition root reads it from its navigation API. */
  launchPath: string
}

class BrowserRuntime {
  private cleanup?: () => void

  constructor(private monitor: RuntimeMonitor) {}

  start(options: BrowserRuntimeOptions): () => void {
    this.cleanup ??= installBrowserRuntime(this.monitor, options, () => { this.cleanup = undefined })
    return this.cleanup
  }
}

const browserRuntime = new BrowserRuntime(runtimeMonitor)

/** Install before constructing clients so captured fetch references are instrumented too. */
export function startBrowserRuntime(options: BrowserRuntimeOptions): () => void {
  return browserRuntime.start(options)
}

export function installBrowserRuntime(monitor: RuntimeMonitor, options: BrowserRuntimeOptions, onStop: () => void = () => {}): () => void {
  if (typeof window === 'undefined') return () => {}
  monitor.setOverview({
    App: options.app, Version: options.version ?? 'Unknown',
    'Launch path': options.launchPath,
    Browser: navigator.userAgent,
    'Secure context': String(globalThis.isSecureContext),
    'Cross-origin isolated': String(globalThis.crossOriginIsolated),
    'Shared workers': typeof SharedWorker === 'undefined' ? 'Unavailable' : 'Available',
    'WebGPU API': 'gpu' in navigator ? 'Available (adapter not checked)' : 'Unavailable',
    'File storage': typeof navigator.storage?.getDirectory === 'function' ? 'Available' : 'Unavailable',
  })
  const update = () => monitor.setOverview({
    Connectivity: navigator.onLine ? 'Online reported by browser' : 'Offline reported by browser',
    Visibility: isVisible() ? 'visible' : 'hidden',
  })
  update()
  window.addEventListener('online', update)
  window.addEventListener('offline', update)
  const stopVisibility = onVisibilityChange(update)
  const original = globalThis.fetch
  const instrumented = Object.assign((input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
    const request = typeof Request !== 'undefined' && input instanceof Request ? input : undefined
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const method = init?.method ?? request?.method ?? 'GET'
    return monitor.run({ name: `${method} ${runtimeUrl(url)}`, kind: 'network', detail: 'Waiting for response headers' }, async task => {
      const response = await original.call(globalThis, input, init)
      task.update({ detail: `HTTP ${response.status}; body consumption is tracked by the calling operation` })
      if (!response.ok) task.finish(new Error('HTTP response failed'))
      return response
    })
  }, original)
  globalThis.fetch = instrumented
  const cleanup = () => {
    if (globalThis.fetch === instrumented) globalThis.fetch = original
    window.removeEventListener('online', update)
    window.removeEventListener('offline', update)
    stopVisibility()
    onStop()
  }
  return cleanup
}

/** Independent browser APIs: no SQLite queries, database opens, or storage writes. */
export async function inspectBrowserStorage(monitor = runtimeMonitor): Promise<void> {
  if (monitor.snapshot().storage.status === 'loading') return
  const result: RuntimeStorage = { status: 'loading', entries: [], notes: [], checkedAt: Date.now() }
  monitor.setStorage({ ...result })
  let finished = false
  const publish = () => { if (!finished) monitor.setStorage({ ...result, entries: [...result.entries], notes: [...result.notes] }) }
  const read = async (name: string, fn: () => Promise<void>) => {
    let expired = false
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        fn(),
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(() => { expired = true; reject(new Error('Timed out')) }, 5_000)
        }),
      ])
    } catch { result.notes.push(`${name}: ${expired ? 'no response within 5 seconds' : 'unavailable or access denied'}`) }
    finally { clearTimeout(timer); publish() }
  }
  await Promise.all([
    read('Storage estimate', async () => {
      const estimate = await navigator.storage.estimate()
      result.usage = estimate.usage
      result.quota = estimate.quota
      result.persisted = await navigator.storage.persisted()
    }),
    read('Local files', async () => {
      const root = await navigator.storage.getDirectory()
      let count = 0
      const walk = async (directory: FileSystemDirectoryHandle, prefix = '', depth = 0): Promise<void> => {
        for await (const handle of directory.values()) {
          if (Date.now() - (result.checkedAt ?? 0) > 4_500 || count >= 200) { result.notes.push('Local file listing limited to 200 entries / 4.5 seconds.'); return }
          count++
          const name = `${prefix}${handle.name}`
          if (handle.kind === 'file') {
            const file = await handle.getFile()
            result.entries.push({ name, kind: 'File', bytes: file.size })
          } else {
            result.entries.push({ name, kind: 'Directory' })
            if (depth < 3) await walk(handle, `${name}/`, depth + 1)
          }
          publish()
        }
      }
      await walk(root)
    }),
    read('IndexedDB names', async () => {
      const databases = await indexedDB.databases()
      result.entries.push(...databases.map(db => ({ name: db.name ?? '(unnamed)', kind: `IndexedDB v${db.version ?? '?'}` })))
    }),
    read('Cache names', async () => {
      result.entries.push(...(await caches.keys()).map(name => ({ name, kind: 'Cache' })))
    }),
    read('Browser locks', async () => {
      const locks = await navigator.locks.query()
      result.entries.push(...(locks.held ?? []).map(lock => ({ name: lock.name ?? '(unnamed)', kind: `Held ${lock.mode} lock` })), ...(locks.pending ?? []).map(lock => ({ name: lock.name ?? '(unnamed)', kind: `Waiting ${lock.mode} lock` })))
    }),
    read('Service workers', async () => {
      const registrations = await navigator.serviceWorker.getRegistrations()
      for (const registration of registrations) {
        for (const worker of [registration.active, registration.waiting, registration.installing]) {
          if (worker) result.entries.push({ name: runtimeUrl(worker.scriptURL), kind: `Service worker: ${worker.state}` })
        }
      }
    }),
  ])
  result.status = result.notes.length ? 'partial' : 'ready'
  publish()
  finished = true
}
